import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { QuotationStateEnums } from 'src/app/enum/quotation-enums';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { QuotationModel } from 'src/app/models/preliminaries/quotation.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { QuotationService } from 'src/app/services/preliminaries/quotation.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-quotation-modal',
  templateUrl: './register-quotation-modal.component.html',
  styleUrls: ['./register-quotation-modal.component.scss'],
  standalone: false
})
export class RegisterQuotationModalComponent implements OnInit {
  public config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly quotationService = inject(QuotationService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly ref = inject(DynamicDialogRef);

  disabledForm: boolean = false;
  formQuotation: FormGroup = new FormGroup({});
  listTypesEquipment: any[] = [];
  listTypesBussinesses1: any[] = [];
  listTypesBussinesses2: any[] = [];
  permissionUpdatable: boolean = false;
  quotationContract: QuotationModel = new QuotationModel();
  quotationStateOpen: number = QuotationStateEnums.open;
  isUpdated: boolean = false;
  subscription: Subscription;

  async ngOnInit(): Promise<void> {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.subscription = input$?.subscribe((inputs: any) => {
      this.quotationContract = inputs.quotationModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.listTypesBussinesses1 = inputs.listTypesBussinesses1;
      this.listTypesBussinesses2 = inputs.listTypesBussinesses2;
      this.listTypesEquipment = inputs.listTypesEquipment;
    });

    await this.validateForm(this.quotationContract);
  }

  close() {
    const data: ModalObjectType = {};
    data.params = { updateInvoice: this.isUpdated };
    this.ref.close(data);
  }

  async send() {
    if (this.disabledForm) {
      this.toastHandlerService.showToast('warning', 'No es posible guardar, la cotización no está ABIERTA', 'Atención');
      return;
    }

    Object.keys(this.formQuotation.controls).forEach(key => {
      const control = this.formQuotation.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formQuotation.valid) {
      const modelQuotation = this.formQuotation.getRawValue();
      this.quotationContract.contact = modelQuotation.contact;
      this.quotationContract.phone1 = modelQuotation.phone1;
      this.quotationContract.phone2 = modelQuotation.phone2;
      this.quotationContract.addressConstructionSite = modelQuotation.addressConstructionSite;
      this.quotationContract.email = modelQuotation.email;
      this.quotationContract.observation = modelQuotation.observation;
      this.quotationContract.conceptIdTypeBussiness1 = modelQuotation.conceptIdTypeBussiness1;
      this.quotationContract.conceptIdTypeBussiness2 = modelQuotation.conceptIdTypeBussiness2;
      this.quotationContract.paymentMethod = modelQuotation.paymentMethod;
      this.quotationContract.deliveryTime = modelQuotation.deliveryTime;
      this.quotationContract.conceptIdTypeEquipment = modelQuotation.conceptIdTypeEquipment;
      this.quotationContract.mountingValue = modelQuotation.mountingValue;
      this.quotationContract.observationCommercial = modelQuotation.observationCommercial;
      this.quotationContract.showConsolidatedEquipmentAvailability = modelQuotation.showConsolidatedEquipmentAvailability;

      try {
        this.quotationContract = await firstValueFrom(this.quotationService.updateQuotation(this.quotationContract));
        this.toastHandlerService.showToast('success', 'La cotización fue modificada exitosamente', '');
        this.isUpdated = true;
      } catch (error) {
        console.error('Error al actualizar la cotización.', error);
        return;
      }

      await this.validateForm(this.quotationContract);
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      addressConstructionSite: {
        maxlength: 'El campo dirección obra permite máximo 70 caracteres.'
      },
      contact: {
        maxlength: 'El campo contacto permite máximo 70 caracteres.'
      },
      phone1: {
        minlength: 'El campo teléfono 1 permite minímo 7 caracteres.',
        maxlength: 'El campo teléfono 1 permite máximo 15 caracteres.'
      },
      phone2: {
        minlength: 'El campo teléfono 2 permite minímo 7 caracteres.',
        maxlength: 'El campo teléfono 2 permite máximo 15 caracteres.'
      },
      email: {
        maxlength: 'El campo correo permite máximo 250 caracteres.'
      },
      observation: {
        maxlength: 'El campo observación permite máximo 250 caracteres.'
      },
      conceptIdTypeBussiness1: {
        required: 'El campo tipo negocio 1 es obligatorio.',
        min: 'Debes seleccionar un tipo negocio 1 válido.'
      },
      conceptIdTypeBussiness2: {
        required: 'El campo tipo negocio 2 es obligatorio.',
        min: 'Debes seleccionar un tipo negocio 2 válido.'
      },
      paymentMethod: {
        maxLength: 'El campo método de pago permite máximo 70 caracteres.'
      },
      deliveryTime: {
        maxLength: 'El campo tiempo entrega permite máximo 70 caracteres.'
      },
      conceptIdTypeEquipment: {
        required: 'El campo tipo equipo es obligatorio.',
        min: 'Debes seleccionar un tipo equipo válido.'
      },
      mountingValue: {
        required: 'El campo valor montaje es obligatorio.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formQuotation.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async getTypeBussiness1(typeBussiness1Id: number | null) {
    const previousTypeBussiness1Id = this.quotationContract.conceptIdTypeBussiness1;
    if (previousTypeBussiness1Id === typeBussiness1Id) {
      return;
    }

    if (typeBussiness1Id) {
      this.formQuotation.controls["conceptIdTypeBussiness1"].patchValue(typeBussiness1Id);
    } else {
      this.formQuotation.controls["conceptIdTypeBussiness1"].patchValue(null);
    }

    // this.disabledButtons();
  }

  async getTypeBussiness2(typeBussiness2Id: number | null) {
    const previousTypeBussiness2Id = this.quotationContract.conceptIdTypeBussiness2;
    if (previousTypeBussiness2Id === typeBussiness2Id) {
      return;
    }

    if (typeBussiness2Id) {
      this.formQuotation.controls["conceptIdTypeBussiness2"].patchValue(typeBussiness2Id);
    } else {
      this.formQuotation.controls["conceptIdTypeBussiness2"].patchValue(null);
    }

    // this.disabledButtons();
  }

  async getTypeEquipment(typeEquipmentId: number | null) {
    const previousTypeBussiness1Id = this.quotationContract.conceptIdTypeEquipment;
    if (previousTypeBussiness1Id === typeEquipmentId) {
      return;
    }

    if (typeEquipmentId) {
      this.formQuotation.controls["conceptIdTypeEquipment"].patchValue(typeEquipmentId);
    } else {
      this.formQuotation.controls["conceptIdTypeEquipment"].patchValue(null);
    }

    // this.disabledButtons();
  }

  async validateForm(quotation: QuotationModel) {
    this.formQuotation = this.formBuilder.group({
      addressConstructionSite: [quotation.addressConstructionSite, [Validators.maxLength(70)]],
      contact: [quotation.contact, [Validators.maxLength(70)]],
      phone1: [quotation.phone1, [Validators.minLength(7), Validators.maxLength(15)]],
      phone2: [quotation.phone2, [Validators.minLength(7), Validators.maxLength(15)]],
      email: [quotation.email, [Validators.maxLength(250)]],
      observation: [quotation.observation, [Validators.maxLength(250)]],
      observationCommercial: [quotation.observationCommercial, []],
      conceptIdTypeBussiness1: [quotation.conceptIdTypeBussiness1, [Validators.required, Validators.min(1)]],
      conceptIdTypeBussiness2: [quotation.conceptIdTypeBussiness2, [Validators.required, Validators.min(1)]],
      paymentMethod: [quotation.paymentMethod, [Validators.maxLength(70)]],
      deliveryTime: [quotation.deliveryTime, [Validators.maxLength(70)]],
      conceptIdTypeEquipment: [quotation.conceptIdTypeEquipment, [Validators.required, Validators.min(1)]],
      mountingValue: [quotation.mountingValue, [Validators.required]],
      showConsolidatedEquipmentAvailability: [quotation.showConsolidatedEquipmentAvailability],
    });

    this.quotationContract.quotationStateId == this.quotationStateOpen ? setTimeout(() => this.formQuotation.enable(), 100) :
      setTimeout(() => this.formQuotation.disable(), 100);
  }
}
